/**
 * SceneTreeDropArea — composition surface for Work Package B.
 *
 * Spec: LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §9 + §10
 * Phase: INIT-2026-Q3-SCENE-HIERARCHY Work Package B (drop-crs-integrator)
 *
 * Composes:
 *   - `LayerDropZone`             (drag-and-drop surface, Phase E)
 *   - `PreflightCard`             (inspection display, Phase E)
 *   - `CrsReconciliationWizard`   (CRS decision, Phase D)
 *   - `dropPipelineReducer`       (state machine, WP-B)
 *   - `sceneHierarchyStore.recordAlignedToEdge` (audit edge, WP-B)
 *
 * The component manages the complete lifecycle from file drop through
 * CRS reconciliation to pin, using dependency-injected adapters for the
 * platform SDK calls so real vs. mock implementations swap in cleanly.
 *
 * Token-driven styling only — no raw hex or pixel values.
 */

import {
  type ReactNode,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { LayerDropZone } from './LayerDropZone';
import { PreflightCard } from './PreflightCard';
import { dropPipelineReducer, DROP_PIPELINE_INITIAL } from './dropPipelineReducer';
import type { PreflightInspection } from './preflightTypes';
import { convertVectorFileToPmtiles } from './converterDropClient';
import { ensureInspectableUrl } from './ensureInspectableUrl';
import { importGlayerFile } from './glayerImportClient';
import { CrsReconciliationWizard } from '../crs/CrsReconciliationWizard';
import { previewAlignment as realPreviewAlignment } from '../crs/alignPreviewClient';
import { persistAlignedToEdge } from '../crs/alignedEdgeClient';
import { createRealPinAdapter } from './realPinAdapter';
import type { CrsReconciliation, ReconciliationDecision } from '../crs/wizardTypes';
import { useSceneHierarchyStore } from '@/state/sceneHierarchyStore';
import type { AlignedToEdgeRecord } from '@/state/sceneHierarchyStore';
import type { SceneItem, SceneSlot } from '@/sdk-stub/gtwin-sdk';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SceneTreeDropAreaProps {
  /** The scene tree or arbitrary children to wrap. */
  readonly children: ReactNode;
  /** gTwin ID for audit-edge recording and pin calls. */
  readonly gtwinId: string;
  /** Identity of the current user — written into decision records. */
  readonly currentUser: string;
  /**
   * Async adapter called to perform the SDK pin.
   *
   * Default: real adapter calling `POST /api/v1/gtwins/{gtwinId}/scene/items`
   * via the shared `gtwinClient` singleton.
   * Tests can inject a mock to avoid real HTTP calls.
   */
  readonly pinAdapter?: (
    inspection: PreflightInspection,
    decision: ReconciliationDecision | null,
  ) => Promise<SceneItem>;
  /**
   * Optional converter hook for tests. Defaults to `convertVectorFileToPmtiles`.
   */
  readonly convertAdapter?: (
    file: File,
    metadata: { readonly crs?: string; readonly bbox?: readonly [number, number, number, number] },
    sidecars?: readonly File[],
  ) => Promise<{
    readonly pmtilesUrl: string;
    readonly sourceCrs: string;
    readonly bbox: [number, number, number, number];
    readonly title: string;
  }>;
  /** Optional import hook for tests. Defaults to platform `importGlayerFile`. */
  readonly importGlayerAdapter?: (file: File) => Promise<{ item: SceneItem; slot: SceneSlot }>;
  /** Called after a successful pin so the shell can refresh catalog + mount. */
  readonly onPinSuccess?: (item: SceneItem, slot: SceneSlot) => void;
  /**
   * Async adapter that calls `POST /align/preview` (or its mock).
   *
   * Default: real adapter calling `POST /api/v1/gtwins/{gtwinId}/align/preview`.
   * Receives the inspection and gtwinId; returns a `CrsReconciliation` triage.
   * Tests can inject a mock (any extra arguments are ignored by mocks).
   */
  readonly previewAlignment?: (
    inspection: PreflightInspection,
    gtwinId: string,
  ) => Promise<CrsReconciliation>;
  /** Forwarded to the root drop-zone wrapper for test queries. */
  'data-testid'?: string;
}

// ─── Overlay styles (token-driven) ───────────────────────────────────────────

const S = {
  dropAreaRoot: {
    position: 'relative' as const,
    width: '100%',
    height: '100%',
  },
  overlay: {
    position: 'absolute' as const,
    inset: 0,
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'color-mix(in srgb, var(--color-scrim) 40%, transparent)',
  },
  successBanner: {
    position: 'absolute' as const,
    bottom: 'var(--spacing-md)',
    right: 'var(--spacing-md)',
    zIndex: 200,
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-secondary-container)',
    color: 'var(--color-on-secondary-container)',
    borderRadius: 'var(--shape-corner-full)',
    fontFamily: 'var(--typography-font-family-base)',
    fontSize: 'var(--typography-label-md-size, 0.875rem)',
    boxShadow: 'var(--elevation-level2)',
    pointerEvents: 'none' as const,
  },
  errorBanner: {
    position: 'absolute' as const,
    bottom: 'var(--spacing-md)',
    right: 'var(--spacing-md)',
    zIndex: 200,
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-error-container)',
    color: 'var(--color-on-error-container)',
    borderRadius: 'var(--shape-corner-medium)',
    fontFamily: 'var(--typography-font-family-base)',
    fontSize: 'var(--typography-label-md-size, 0.875rem)',
    boxShadow: 'var(--elevation-level2)',
  },
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Composes `LayerDropZone`, `PreflightCard`, and `CrsReconciliationWizard`
 * around any children (typically `SceneTreeV2Container`).
 *
 * Every pipeline stage has a defined visual:
 * - `idle`                 — children only, no overlays
 * - `preflight-ready`      — PreflightCard overlay
 * - `awaiting-crs-decision`— CrsReconciliationWizard overlay
 * - `ready-to-pin`         — PreflightCard overlay (CRS resolved)
 * - `pinning`              — PreflightCard overlay with disabled controls
 * - `pinned`               — children + brief success toast
 * - `cancelled`            — children only (card dismissed)
 * - `error`                — children + error toast
 */
export function SceneTreeDropArea({
  children,
  gtwinId,
  currentUser,
  pinAdapter: pinAdapterProp,
  convertAdapter: convertAdapterProp,
  importGlayerAdapter: importGlayerAdapterProp,
  onPinSuccess,
  previewAlignment: previewAlignmentProp,
  'data-testid': testId = 'scene-tree-drop-area',
}: SceneTreeDropAreaProps) {
  const [state, dispatch] = useReducer(dropPipelineReducer, DROP_PIPELINE_INITIAL);
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | undefined>();

  // Memoized real adapters — only recreated when gtwinId changes.
  const defaultPinAdapter = useMemo(() => createRealPinAdapter(gtwinId), [gtwinId]);
  const pinAdapter = pinAdapterProp ?? defaultPinAdapter;
  const convertAdapter = convertAdapterProp ?? convertVectorFileToPmtiles;
  const importGlayerAdapter =
    importGlayerAdapterProp ??
    (async (file: File) => {
      const result = await importGlayerFile(gtwinId, file);
      const item = result.item as unknown as SceneItem;
      const slot = (typeof result.item.slot === 'string'
        ? result.item.slot
        : 'realityData') as SceneSlot;
      return { item, slot };
    });

  const defaultPreviewAlignment = useCallback(
    (inspection: PreflightInspection, gid: string) =>
      realPreviewAlignment(gid, {
        id: inspection.id,
        detectedKind: inspection.detectedKind,
        detectedSlot: inspection.detectedSlot,
        crs: inspection.crs,
        bbox: inspection.bbox,
      }),
    [],
  );
  const previewAlignmentFn = previewAlignmentProp ?? defaultPreviewAlignment;

  // ── Preflight handler ───────────────────────────────────────────────────────

  const handlePreflight = useCallback(
    async (inspection: PreflightInspection) => {
      try {
        if (inspection.detectedKind === 'glayer') {
          const triage = await previewAlignmentFn(
            {
              ...inspection,
              crs: inspection.crs ?? {
                code: 'EPSG:4326',
                coordinateMode: 'georeferenced',
              },
            },
            gtwinId,
          );
          dispatch({ kind: 'preflight', inspection, triage });
          return;
        }
        const triage = await previewAlignmentFn(inspection, gtwinId);
        dispatch({ kind: 'preflight', inspection, triage });
      } catch (err) {
        dispatch({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Preview alignment failed',
        });
      }
    },
    [previewAlignmentFn, gtwinId],
  );

  // ── Pin handler ─────────────────────────────────────────────────────────────

  const handlePin = useCallback(async () => {
    if (!state.inspection) return;
    dispatch({ kind: 'pin', sceneItemInput: {} });
    setPipelineBusy(true);
    setBusyLabel('Pinning…');
    try {
      const ready = await ensureInspectableUrl(state.inspection);
      const item = await pinAdapter(ready, state.crsDecision);
      dispatch({ kind: 'pin-success', sceneItemId: item.id });
      onPinSuccess?.(item, ready.detectedSlot);
    } catch (err) {
      dispatch({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Pin failed',
      });
    } finally {
      setPipelineBusy(false);
      setBusyLabel(undefined);
    }
  }, [state.inspection, state.crsDecision, pinAdapter, onPinSuccess]);

  // ── Convert-first handler ───────────────────────────────────────────────────

  const handleConvert = useCallback(async () => {
    if (!state.inspection?.droppedFile) {
      dispatch({ kind: 'error', message: 'No local file available to convert' });
      return;
    }
    setPipelineBusy(true);
    setBusyLabel('Converting…');
    try {
      const file = state.inspection.droppedFile;
      const converted = await convertAdapter(
        file,
        {
          crs: state.inspection.crs?.code,
          bbox:
            state.inspection.bbox && state.inspection.bbox.length === 4
              ? ([
                  state.inspection.bbox[0],
                  state.inspection.bbox[1],
                  state.inspection.bbox[2],
                  state.inspection.bbox[3],
                ] as [number, number, number, number])
              : undefined,
        },
        state.inspection.droppedSidecars ?? [],
      );

      const updatedInspection: PreflightInspection = {
        ...state.inspection,
        url: converted.pmtilesUrl,
        fileName: converted.title,
        detectedKind: 'pmtiles',
        detectedLayerKind: 'vector',
        detectedSlot: 'featureLayers',
        crs: {
          code: converted.sourceCrs,
          coordinateMode: 'georeferenced',
        },
        bbox: converted.bbox,
        recommendedAction: 'pin',
        alignDirective: 'identity',
        warnings: [
          ...state.inspection.warnings,
          'Converted to PMTiles — ready to pin.',
        ],
        droppedFile: undefined,
      };

      const triage = await previewAlignmentFn(updatedInspection, gtwinId);
      dispatch({ kind: 'preflight', inspection: updatedInspection, triage });

      if (triage.directive === 'identity') {
        dispatch({ kind: 'pin', sceneItemInput: {} });
        setBusyLabel('Pinning…');
        const item = await pinAdapter(updatedInspection, null);
        dispatch({ kind: 'pin-success', sceneItemId: item.id });
        onPinSuccess?.(item, updatedInspection.detectedSlot);
      }
    } catch (err) {
      dispatch({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Convert failed',
      });
    } finally {
      setPipelineBusy(false);
      setBusyLabel(undefined);
    }
  }, [
    state.inspection,
    convertAdapter,
    previewAlignmentFn,
    gtwinId,
    pinAdapter,
    onPinSuccess,
  ]);

  const handleGlayerImport = useCallback(async () => {
    if (!state.inspection?.droppedFile) {
      dispatch({ kind: 'error', message: 'No .glayer file to import' });
      return;
    }
    dispatch({ kind: 'pin', sceneItemInput: {} });
    setPipelineBusy(true);
    setBusyLabel('Importing…');
    try {
      const { item, slot } = await importGlayerAdapter(state.inspection.droppedFile);
      dispatch({ kind: 'pin-success', sceneItemId: item.id });
      onPinSuccess?.(item, slot);
    } catch (err) {
      dispatch({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Glayer import failed',
      });
    } finally {
      setPipelineBusy(false);
      setBusyLabel(undefined);
    }
  }, [state.inspection, importGlayerAdapter, onPinSuccess]);

  // ── CRS decision handler ────────────────────────────────────────────────────

  const handleCrsDecision = useCallback(
    (decision: ReconciliationDecision) => {
      dispatch({ kind: 'crs-decision', decision });

      // After the reducer runs, the alignedToEdgeId will be minted. Record
      // the edge immediately using the decision data we have here.
      if (decision.optionKey !== 'reject' && state.crsTriage && state.inspection) {
        // The ID will be minted by the reducer on this dispatch, but we need
        // to generate a consistent one. We read it from the store after dispatch
        // via a microtask, or we can pre-mint and pass through.
        // Since the reducer mints its own UUID, we call recordAlignedToEdge
        // after a short microtask so the new state is visible.
        const methodMap: Record<
          string,
          AlignedToEdgeRecord['method']
        > = {
          'align-viewer': 'viewer-matrix',
          'align-converter': 'converter-reproject',
          'accept-anchor': 'anchor-promotion',
          'align-on-read': 'reproject-on-read',
        };
        const method: AlignedToEdgeRecord['method'] =
          methodMap[decision.optionKey] ?? 'viewer-matrix';

        // Record the edge after reducer has processed the decision
        queueMicrotask(() => {
          const store = useSceneHierarchyStore.getState();
          const recordFn = store.recordAlignedToEdge;
          if (!recordFn) return;

          // Read the newly-set alignedToEdgeId from the store state — it's in
          // React state, not the Zustand store. We generate a fresh UUID here
          // (consistent approach: let each call be its own edge; the reducer's
          // UUID is for the pipeline state tracking only).
          const edgeId = crypto.randomUUID();

          const edge: AlignedToEdgeRecord = {
            id: edgeId,
            fromNodeId: state.inspection?.id ?? gtwinId,
            toNodeId: 'node:project-anchor',
            method,
            source: { crs: state.crsTriage?.source.crs ?? '' },
            target: { crs: state.crsTriage?.target.horizontalCrs ?? '' },
            matrix: decision.viewerMatrix ?? undefined,
            scaleMode: decision.scaleMode,
            decisionAt: decision.decisionAt,
            decisionBy: decision.decisionBy,
          };
          recordFn(edge);
          // C4/G4: also persist the edge so the CRS decision survives reload.
          // Best-effort — never blocks the UI; the session log above is the
          // source of truth for immediate rendering.
          void persistAlignedToEdge(gtwinId, edge);
        });
      }
    },
    [state.crsTriage, state.inspection, gtwinId],
  );

  // ── Cancel handler ──────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    dispatch({ kind: 'cancel' });
  }, []);

  // ── Reconcile CRS from preflight card ───────────────────────────────────────

  const handleReconcileCrs = useCallback(() => {
    // The preflight card calls this when the user clicks "Open CRS Wizard".
    // We dispatch a synthetic re-preflight with a reproject triage to open wizard.
    // In practice this is already in awaiting-crs-decision from the initial preflight;
    // this handler is for the edge case where the card is shown with reconcile-crs
    // action and the user explicitly opens the wizard.
    // The state should already be in preflight-ready with triage available; we
    // re-dispatch the preflight event with the existing triage but force reproject.
    if (state.inspection && state.crsTriage) {
      dispatch({
        kind: 'preflight',
        inspection: state.inspection,
        triage: { ...state.crsTriage, directive: 'reproject' },
      });
    }
  }, [state.inspection, state.crsTriage]);

  // ── Error handler from drop zone ────────────────────────────────────────────

  const handleDropError = useCallback((err: Error) => {
    dispatch({ kind: 'error', message: err.message });
  }, []);

  // ── Render helpers ───────────────────────────────────────────────────────────

  const showPreflightCard =
    state.stage === 'preflight-ready' ||
    state.stage === 'ready-to-pin' ||
    state.stage === 'pinning' ||
    (state.stage === 'error' && state.inspection !== null);

  const showWizard = state.stage === 'awaiting-crs-decision';
  const showSuccessToast = state.stage === 'pinned';
  const showErrorToast = state.stage === 'error' && !state.inspection;

  return (
    <div
      style={S.dropAreaRoot}
      data-testid={testId}
    >
      <LayerDropZone
        targetKind="scene-tree-node"
        onPreflight={(inspection) => { void handlePreflight(inspection); }}
        onError={handleDropError}
        disabled={state.stage === 'pinning' || pipelineBusy}
      >
        {children}
      </LayerDropZone>

      {/* PreflightCard overlay — shown for preflight-ready, ready-to-pin, pinning */}
      {showPreflightCard && state.inspection ? (
        <div
          style={S.overlay}
          data-testid="drop-pipeline-overlay"
        >
          <PreflightCard
            inspection={state.inspection}
            onPin={() => { void handlePin(); }}
            onCancel={handleCancel}
            onReconcileCrs={handleReconcileCrs}
            onConvert={() => { void handleConvert(); }}
            onImportGlayer={() => { void handleGlayerImport(); }}
            busy={state.stage === 'pinning' || pipelineBusy}
            busyLabel={busyLabel}
            errorMessage={state.stage === 'error' ? state.errorMessage : null}
          />
        </div>
      ) : null}

      {/* CrsReconciliationWizard — shown as Dialog portal when awaiting decision */}
      {showWizard && state.crsTriage ? (
        <CrsReconciliationWizard
          open
          triage={state.crsTriage}
          currentUser={currentUser}
          onDecision={handleCrsDecision}
          onClose={handleCancel}
          data-testid="crs-reconciliation-wizard"
        />
      ) : null}

      {/* Success toast — brief indicator after pin completes */}
      {showSuccessToast ? (
        <div
          style={S.successBanner}
          role="status"
          aria-live="polite"
          data-testid="pin-success-toast"
        >
          Layer pinned successfully
        </div>
      ) : null}

      {/* Error toast — shown when stage === "error" */}
      {showErrorToast ? (
        <div
          style={S.errorBanner}
          role="alert"
          data-testid="drop-error-toast"
        >
          {state.errorMessage ?? 'An error occurred'}
        </div>
      ) : null}
    </div>
  );
}
